import { Box, Divider, Grid, HStack, Stack, Text } from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import { TranslateIcon } from "modules/icons";
import React, { memo, useRef } from "react";
import colors from "app/colors";
import useMessageBackground from "modules/hooks/use-message-background";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import MessageFiles from "./message-files";
import MessageGifViewer from "./message-gif-viewer";
import MessageReactions from "./message-reactions";
import MessageText from "./message-text";
import QuotedMessage from "./quoted-message";
import EmailMessage from "./email-message";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  overrideMessageBackgroundColor?: string;
}

const ReadonlyMessageBody: React.FC<IProps> = ({
  messageThread,
  message,
  overrideMessageBackgroundColor,
}) => {
  const { messageBackground } = useMessageBackground({ message });
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const gifList = message.gifList;
  const reactionList = message.reactionList;
  const quotedMessageList = message.quotedMessageList;

  return (
    <Box
      alignItems="start"
      bg={overrideMessageBackgroundColor ?? messageBackground.backgroundColor}
      roundedTopRight="2xl"
      roundedBottomRight="2xl"
      roundedBottomLeft="2xl"
      transition={messageBackground.transition}
      w="100%"
      pt="10px"
      pr="12px"
      pb="10px"
      pl={isViewingTranslatedMessage ? "4px" : "12px"}
    >
      {message.deleted ? (
        <Text fontStyle="italic" color={colors.grayMain} fontSize="sm">
          This message has been deleted
        </Text>
      ) : (
        <Grid ref={messageContainerRef}>
          <Box gridRow={1} gridColumn={1}>
            {!!quotedMessageList.length && (
              <Stack mb={0.5} spacing={4}>
                {quotedMessageList.map((quotedMessage, index) => (
                  <Box key={index} pl={isViewingTranslatedMessage ? 3 : 0}>
                    <QuotedMessage
                      messageThread={messageThread}
                      quotedMessage={quotedMessage}
                    />
                  </Box>
                ))}
              </Stack>
            )}
            {message.text && (
              <HStack alignItems="flex-start">
                {isViewingTranslatedMessage && (
                  <TranslateIcon boxSize={3} fill={colors.grayMain} mt={1} />
                )}
                <Box
                  color={colors.blackSecondary}
                  fontWeight={400}
                  fontSize={14}
                  sx={{ margin: "0 !important" }}
                >
                  <MessageText
                    messageThread={messageThread}
                    message={message}
                  />
                </Box>
              </HStack>
            )}
            {message.type === MessageType.Email && (
              <EmailMessage
                messageThread={messageThread}
                message={message}
                messageState="read-only"
              />
            )}
            {gifList.length > 0 && (
              <Box pb="8px">
                {gifList.map((gif, index) => (
                  <MessageGifViewer
                    key={index}
                    messageThread={messageThread}
                    message={message}
                    gif={gif}
                  />
                ))}
              </Box>
            )}
            {message.fileList.length > 0 && (
              <>
                {message.type === MessageType.Email && (
                  <HStack w="full">
                    <Text
                      w={165}
                      color={colors.gray50}
                      fontSize={12}
                      fontWeight={700}
                    >
                      Email Attachments
                    </Text>
                    <Divider borderColor={colors.gray30} borderStyle="dashed" />
                  </HStack>
                )}
                <MessageFiles
                  messageThread={messageThread}
                  message={message}
                  messageState="read-only"
                />
              </>
            )}
            <HStack>
              {!!reactionList.length && (
                <Box py={1} pl={isViewingTranslatedMessage ? 3 : 0}>
                  <MessageReactions message={message} />
                </Box>
              )}
            </HStack>
          </Box>
        </Grid>
      )}
    </Box>
  );
};

export default memo(ReadonlyMessageBody);
